import {
  ArrowDown10,
  ArrowDownZA,
  ArrowUp10,
  ArrowUpAZ,
  ArrowUpDown,
  Check,
  Pencil,
  MessageSquarePlus,
  XCircle,
} from 'lucide-react';
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Loader from '../Loader';
import { useTheme } from '../../hooks/useTheme';
import AddRemarkModal from './Remarks';
import { addInterviewCommentThunk } from '../../store/thunks/InterviewThunk';

// Column key → header label mapping
const COL_LABELS = {
  checkbox: null, // always shown, no label match needed
  name: 'Candidate Name',
  job_role: 'Job Role',
  email: 'Email ID',
  contact: 'Contact Number',
  applied_on: 'Applied On', // MY_UPLOADS uses "Uploaded On" alias too
  uploaded_on: 'Applied On',
  notice_period: 'Notice Period',
  source: 'Source',
  resume_score: 'Resume Score',
};

// Check whether a column should be visible given the currentCols array from Redux.
// currentCols is an array of objects like [{ "Candidate Name": true }, { "Email Id": false }, ...]
// If currentCols is undefined/empty we show everything (graceful fallback).
const isColVisible = (currentCols, colLabel) => {
  if (!currentCols || currentCols.length === 0) return true;
  const entry = currentCols.find((obj) => Object.keys(obj)[0] === colLabel);
  if (!entry) return true; // not in list → show by default
  return entry[colLabel] === true;
};

function CommonTable({
  combinedData = [],
  startIndex,
  endIndex,
  selectAllClicked = false,
  handleSelectAllCandidates,
  handleSelectCandidate,
  handleReportClick,
  editingCandidateId,
  editingField,
  tempValue,
  setTempValue,
  onEdit,
  onSave,
  sortConfig,
  onSort,
  resume_score_threshold,
  // Optional: pass currentCols directly; if omitted we read from Redux
  currentCols: currentColsProp,
  // activeSubTab lets us pick the right Redux col config when not passed directly
  activeSubTab: activeSubTabProp,
}) {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [remarkedIds, setRemarkedIds] = useState(new Set());

  const isCandidateLoading = useSelector(
    (state) =>
      state.candidateManagementSlice.loading ||
      state.candidateManagementSlice.candidatesLoading,
  );

  const { theme: isDarkMode } = useTheme();
  // Read column visibility from Redux when not passed as a prop
  const reduxActiveTab = useSelector(
    (state) => state.candidateManagementSlice.activeTab,
  );
  const reduxActiveSubTab = useSelector(
    (state) => state.candidateManagementSlice.activeSubTab,
  );
  const activeSubTab = activeSubTabProp ?? reduxActiveSubTab;
  const activeSubSubTab = useSelector(
    (state) => state.candidateManagementSlice.activeSubSubTab,
  );
  const reduxCurrentCols = useSelector(
    (state) =>
      state.filterSlice.columnVisibility?.[reduxActiveTab]?.[activeSubTab],
  );
  const currentCols = currentColsProp ?? reduxCurrentCols;

  const isAdminLoggedIn = useSelector(
    (state) => state.interviewCommentsSlice.isAdminLoggedIn,
  );

  // Convenience: is a named column visible?
  const show = (label) => isColVisible(currentCols, label);

  // ─── Sort icons ──────────────────────────────────────────────────────────────

  const SortIcon = ({ colKey }) => {
    const cls =
      'w-4 h-4 ml-2 cursor-pointer dark:hover:text-white hover:text-gray-300';
    if (sortConfig?.key !== colKey)
      return <ArrowUpDown className={`${cls} opacity-40`} />;
    if (sortConfig?.direction === 'asc') return <ArrowUpAZ className={cls} />;
    if (sortConfig?.direction === 'desc')
      return <ArrowDownZA className={cls} />;
    return <ArrowUpDown className={`${cls} opacity-40`} />;
  };

  const ScoreSortIcon = ({ colKey }) => {
    const cls =
      'w-4 h-4 ml-2 cursor-pointer dark:hover:text-white hover:text-gray-300';
    if (sortConfig?.key !== colKey)
      return <ArrowUpDown className={`${cls} opacity-40`} />;
    if (sortConfig?.direction === 'asc') return <ArrowUp10 className={cls} />;
    if (sortConfig?.direction === 'desc')
      return <ArrowDown10 className={cls} />;
    return <ArrowUpDown className={`${cls} opacity-40`} />;
  };

  // ─── Cell helpers ─────────────────────────────────────────────────────────────

  const getStatusBadge = (candidate) => {
    if (candidate._tableSource === 'MY_UPLOADS') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValidEmail = (e) =>
        e && typeof e === 'string' && emailRegex.test(e);
      if (
        !isValidEmail(candidate.candidate_email) ||
        candidate.score === null
      ) {
        return {
          label: 'Invalid',
          cls: 'bg-red-500/10 text-red-400 border-red-500/30',
        };
      }
      if (candidate.score >= resume_score_threshold) {
        return {
          label: 'Shortlisted',
          cls: 'bg-green-500/10 text-green-400 border-green-500/30',
        };
      }
      return {
        label: 'Retrofit',
        cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
      };
    }
    if (candidate.score >= resume_score_threshold) {
      return {
        label: 'Shortlisted',
        cls: 'bg-green-500/10 text-green-400 border-green-500/30',
      };
    }
    return {
      label: 'Retrofit',
      cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    };
  };

  const getDate = (candidate) => {
    if (candidate._tableSource === 'MY_UPLOADS') {
      return candidate?.uploaded_on?.split('T')[0] ?? '-';
    }
    return candidate?.applied_at?.split('T')[0] ?? '-';
  };

  // Outreached rows come from the sourcing service, not from a resume upload — the
  // ATS endpoints behind inline editing don't know them, so the pencils are hidden.
  const canEditContact = (candidate) => candidate._tableSource !== 'OUTREACHED';

  const getOriginBadge = (candidate) => {
    if (candidate._tableSource === 'MY_UPLOADS') {
      return (
        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs border bg-blue-500/10 text-blue-400 border-blue-500/30 w-20 truncate">
          Uploaded
        </span>
      );
    }
    if (candidate._tableSource === 'OUTREACHED') {
      return (
        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs border bg-pink-500/10 text-pink-400 border-pink-500/30 w-20 truncate">
          Outreach
        </span>
      );
    }
    return (
      <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs border bg-purple-500/10 text-purple-400 border-purple-500/30 w-20 truncate">
        {candidate?.source_of_application || 'Sourced'}
      </span>
    );
  };

  // ─── Shared header / cell class ───────────────────────────────────────────────

  const thCls =
    'text-left px-4 py-4  text-sm font-medium dark:text-[#CCCCCC] text-textSecondary';
  const tdCls = 'p-4 text-sm dark:text-[#CCCCCC] text-textSecondary';

  // ─── Loading state ────────────────────────────────────────────────────────────

  if (isCandidateLoading) {
    return (
      <div className="w-full h-[70vh] flex flex-row items-center justify-center">
        <Loader />
      </div>
    );
  }

  const paginatedData = combinedData.slice(startIndex, endIndex);
  // How many visible body columns (excluding checkbox) for the empty-state colspan
  const visibleColCount =
    1 + // checkbox always
    (show('Candidate Name') ? 1 : 0) +
    (show('Job Role') ? 1 : 0) +
    (show('Email Id') ? 1 : 0) +
    (show('Contact Number') ? 1 : 0) +
    (show('Applied On') || show('Uploaded On') ? 1 : 0) +
    (show('Notice Period') ? 1 : 0) +
    (show('Source') ? 1 : 0) +
    (show('Resume Score') ? 1 : 0);

  return (
    <div className="flex-1 min-h-[70vh] max-h-full rounded-lg border-gray-700/50 overflow-y-auto">
      <style>{`
        /* Chrome, Edge, Safari */
        /* Light theme only */
        .light ::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }

        .light ::-webkit-scrollbar-track {
          background: transparent;
        }

        .light ::-webkit-scrollbar-thumb {
          background: #e5e7eb; /* gray-600 */
          border-radius: 100px;
        }

        .light ::-webkit-scrollbar-thumb:hover {
          background: #d1d5db; /* gray-700 */
        }

        .light ::-webkit-scrollbar-corner {
          background: transparent;
        }

        /* Firefox */
        .light * {
          scrollbar-width: thin;
          scrollbar-color: #e5e7eb  transparent;
        }
        ::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }

        ::-webkit-scrollbar-track {
          background: transparent;
        }

        ::-webkit-scrollbar-thumb {
          background: #4b5563; /* gray-600 */
          border-radius: 100px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #374151; /* gray-700 */
        }

        ::-webkit-scrollbar-corner {
          background: transparent;
        }

        /* Firefox */
        * {
          scrollbar-width: thin;
          scrollbar-color: #4b5563 transparent; /* thumb track */
        }
      `}</style>
      <table className="w-full relative">
        <thead
          className=" z-10 bg-bgSecondary"
        >
          {/* <div className="absolute bottom-0 left-0 w-full h-[1px] bg-[#5a447960] pointer-events-none"></div> */}
          <tr className='border-b border-[#5A4479D4]'>
            {/* Checkbox — always visible */}
            <th className={`${thCls} w-12 py-4`}>
              {activeSubSubTab != 'Invalid' && combinedData.length > 0 && (
                <input
                  type="checkbox"
                  className="rounded bg-gray-700 border-gray-600 w-4 h-4 accent-blue-600"
                  checked={selectAllClicked}
                  onChange={(e) => handleSelectAllCandidates(e.target.checked)}
                  title="Select All"
                />
              )}
            </th>

            {show('Candidate Name') && (
              <th
                className={`${thCls} cursor-pointer`}
                onClick={() => onSort('name')}
              >
                <div className="flex items-center">
                  Candidate Name <SortIcon colKey="name" />
                </div>
              </th>
            )}

            {show('Job Role') && (
              <th
                className={`${thCls} cursor-pointer`}
                onClick={() => onSort('job_role')}
              >
                <div className="flex items-center">
                  Job Role 
                  {/* <SortIcon colKey="job_role" /> */}
                </div>
              </th>
            )}

            {show('Email Id') && <th className={thCls}>Email ID</th>}

            {show('Contact Number') && (
              <th className={thCls}>Contact Number</th>
            )}

            {/* "Applied On" for ALL/SOURCED, "Uploaded On" for MY_UPLOADS —
                both map to the date column; show if either label is visible */}
            {show('Applied On') && (
              <th
                className={`${thCls} cursor-pointer`}
                onClick={() => onSort('date')}
              >
                <div className="flex items-center">
                  Applied On
                  <SortIcon colKey="date" />
                </div>
              </th>
            )}

            {show('Notice Period') && (
              <th className={thCls}>
                <div className="flex items-center">Notice Period</div>
              </th>
            )}

            {show('Source') && (
              <th className={thCls}>
                <div className="flex items-center">Source</div>
              </th>
            )}

            {show('Resume Score') && (
              <th
                className={`${thCls} cursor-pointer`}
                onClick={() => onSort('score')}
              >
                <div className="flex items-center"> 
                  Resume Score <ScoreSortIcon colKey="score" />
                </div>
              </th>
            )}

            {show('remarks') && (
              <th className={thCls}>
                <div className="flex items-center">Remark</div>
              </th>
            )}
          </tr>
        </thead>

        {/* ── TBODY ── */}
        <tbody>
          {combinedData.length === 0 && (
            <tr>
              <td colSpan={visibleColCount} className="h-72 text-center">
                <div className="flex h-full flex-col items-center justify-center">
                  <h2 className="text-lg font-medium text-gray-400 mb-1">
                    No candidates found
                  </h2>
                  <p className="text-sm text-gray-400">
                    Try adjusting your filters or search terms to see more
                    results.
                  </p>
                </div>
              </td>
            </tr>
          )}

          {paginatedData.map((candidate, index) => {
            const status = getStatusBadge(candidate);
            const isMyUpload = candidate._tableSource === 'MY_UPLOADS';

            return (
              <tr
                key={`${candidate._tableSource}-${candidate._id || candidate.unique_id || candidate.candidate_email}-${index}`}
                className="border-b dark:border-gray-700/50 border-gray-300/50 dark:hover:bg-gray-800 hover:bg-gray-100 transition-colors cursor-pointer group"
                onClick={(e) => {
                  if (
                    e.target.closest('td:nth-child(1)') ||
                    e.target.tagName === 'BUTTON' ||
                    e.target.tagName === 'INPUT' ||
                    e.target.closest('button') ||
                    e.target.closest('input')
                  ) {
                    e.stopPropagation();
                    return;
                  }
                  handleReportClick(candidate);
                }}
              >
                {/* Checkbox — always visible */}
                <td className="p-4">
                  {!(isMyUpload && status.label === 'Invalid') && (
                    <input
                      type="checkbox"
                      className="rounded bg-gray-700 border-gray-600 w-4 h-4 accent-blue-600"
                      checked={candidate.isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectCandidate(candidate);
                      }}
                    />
                  )}
                </td>

                {show('Candidate Name') && (
                  <td className={`${tdCls} font-medium dark:font-medium`}>{candidate.name}</td>
                )}

                {show('Job Role') && (
                  <td className={tdCls}>{candidate.job_role || 'N/A'}</td>
                )}

                {show('Email ID') && (
                  <td className={`relative ${tdCls}`}>
                    <div className="flex items-center space-x-2">
                      {editingCandidateId === candidate._id && editingField === 'email' ? (
                        <input
                          type="email"
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          placeholder="Enter email"
                          autoComplete="off"
                          className="bg-transparent flex-1 border border-gray-600 dark:text-[#CCCCCC] text-textSecondary px-2 py-1 rounded text-sm"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <span
                          className="flex-1 min-w-0 max-w-[280px] truncate block"
                          title={candidate.candidate_email || 'Not Provided'}
                        >
                          {candidate.candidate_email || 'Not Provided'}
                        </span>
                      )}

                      {editingCandidateId === candidate._id && editingField === 'email' ? (
                        <div className="flex space-x-1 flex-shrink-0">
                          <button
                            className="p-1 rounded hover:bg-gray-300 dark:hover:bg-gray-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSave(candidate);
                            }}
                          >
                            <Check size={16} className="text-green-400" />
                          </button>
                          <button
                            className="p-1 rounded hover:bg-gray-300 dark:hover:bg-gray-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSave(null);
                            }}
                          >
                            <XCircle size={16} className="text-red-400" />
                          </button>
                        </div>
                      ) : (
                        canEditContact(candidate) && (
                          <button
                            className="p-1 rounded hover:bg-gray-300 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(candidate, 'email');
                            }}
                          >
                            <Pencil size={16} className="text-gray-400" />
                          </button>
                        )
                      )}
                    </div>
                  </td>
                )}

                {show('Contact Number') && (
                  <td className={`relative ${tdCls}`}>
                    <div className="flex items-center space-x-2">
                      {editingCandidateId === candidate._id &&
                      editingField === 'contact' ? (
                        <input
                          type="text"
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          placeholder="Enter contact number"
                          autoComplete="off"
                          className="bg-transparent flex-1 border border-gray-600 dark:text-[#CCCCCC] text-textSecondary px-2 py-1 rounded text-sm"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <span
                          className="flex-1 min-w-0 max-w-[280px] truncate block"
                          title={
                            candidate.contact_number ||
                            candidate.mobile ||
                            'Not Provided'
                          }
                        >
                          {candidate.contact_number ||
                            candidate.mobile ||
                            'Not Provided'}
                        </span>
                      )}

                      {editingCandidateId === candidate._id &&
                      editingField === 'contact' ? (
                        <div className="flex space-x-1 flex-shrink-0">
                          <button
                            className="p-1 rounded hover:bg-gray-300 dark:hover:bg-gray-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSave(candidate);
                            }}
                          >
                            <Check size={16} className="text-green-400" />
                          </button>
                          <button
                            className="p-1 rounded hover:bg-gray-300 dark:hover:bg-gray-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSave(null);
                            }}
                          >
                            <XCircle size={16} className="text-red-400" />
                          </button>
                        </div>
                      ) : (
                        canEditContact(candidate) && (
                          <button
                            className="p-1 rounded hover:bg-gray-300 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(candidate, 'contact');
                            }}
                          >
                            <Pencil size={16} className="text-gray-400" />
                          </button>
                        )
                      )}
                    </div>
                  </td>
                )}

                {show('Applied On') && (
                  <td className={tdCls}>{getDate(candidate)}</td>
                )}

                {show('Notice Period') && (
                  <td
                    className={`${tdCls} ${!candidate.notice_period ? 'pl-8' : ''}`}
                  >
                    {candidate.notice_period || '-'}
                  </td>
                )}

                {show('Source') && (
                  <td className={tdCls}>{getOriginBadge(candidate)}</td>
                )}

                {show('Resume Score') && (
                  <td className={`${tdCls}`}>
                    {/* No score at all (outreached candidates were never resume-scanned)
                        reads as a dash, not as a zero in a red box. */}
                    {candidate.score === null || candidate.score === undefined ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <div
                        className={`flex items-center justify-center border w-9 p-0.5 rounded-lg ${
                          candidate.score >= 70
                            ? 'border-green-600 dark:text-green-400 text-green-600 bg-[#1BAA0E1A]'
                            : candidate.score >= 30
                              ? 'border-yellow-600 dark:text-yellow-400 text-yellow-600 bg-[#FFC5301A]'
                              : 'border-red-600 dark:text-red-400 text-red-600 bg-[#FF00001A]'
                        }`}
                      >
                        <span>{candidate.score}</span>
                      </div>
                    )}
                  </td>
                )}

                {show('Remarks') && (
                    <td className="py-4 px-4 text-sm">
                      <div className="flex items-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCandidate(candidate);
                            setIsModalOpen(true);
                          }}
                          className="relative hover:scale-110 transition-transform duration-200"
                        >
                          <MessageSquarePlus className="h-5 w-5 text-textSecondary dark:text-gray-400 hover:text-textPrimary dark:hover:text-[#C1A0D0]" />
                          {(candidate.has_remarks || remarkedIds.has(candidate._id)) && (
                            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500" />
                          )}
                        </button>
                      </div>
                    </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      <AddRemarkModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCandidate(null);
        }}
        onSubmit={(remark) => {
          dispatch(
            addInterviewCommentThunk({
              interviewId: selectedCandidate._id,
              commentContent: remark,
              isAdmin: isAdminLoggedIn,
            })
          );
          setRemarkedIds((prev) => new Set(prev).add(selectedCandidate._id));
        }}
        candidateName={selectedCandidate?.name}
        interviewId={selectedCandidate?._id}
      />
    </div>
  );
}

export default CommonTable;
